// Phase I3 — Build-Your-Own panel.
// Interactive market browser. Reads slip.build_your_own_legs (precomputed
// candidates from the I2 BYO scoring path) and lets the user toggle up
// to 3 custom legs; combined odds re-multiply locally.
//
// The architecture spec also describes a per-click invocation of the
// `super-acca-byo-score` Edge Function. That function is a Phase I2 build
// product that may not be deployed in every environment yet — this panel
// degrades gracefully: if no candidates are returned, the user sees an
// empty-state and the page remains coherent.

import { useMemo, useState } from "react";
import { Badge, CategoryChip, Eyebrow } from "../../../components/super-acca";
import { CATEGORY, type CategoryId } from "../../../super-acca/categories";
import { getBrowserClient } from "../../../supabase/browser.ts";
import type {
  BYOMarketCandidate,
  SuperAccaSlip,
} from "../../../super-acca/view-models";

// Phase I5.3 — live BYO scorer response shape (super-acca-byo-score Edge fn).
// Persisting the lock-leg back to bet_builder_slips.build_your_own_legs is
// deferred — needs a dedicated endpoint that mutates the slip, out of scope
// for I5. This panel is read-only/advisory until that endpoint lands.
interface ByoScore {
  ok: boolean;
  compatible: boolean;
  contradicts?: boolean;
  contradicts_with_leg?: number | null;
  reason?: string | null;
  ev_delta?: number | null;
  value_edge_tier?: "sharp" | "strong" | "value" | "none";
  sharp_money_signal?: "confirms" | "challenges" | "neutral";
  edge_pct?: number | null;
  model_prob?: number | null;
  market_prob?: number | null;
}

type ScoreState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok"; score: ByoScore }
  | { kind: "error"; message: string };

export interface BuildYourOwnProps {
  slip: SuperAccaSlip;
}

type Filter = "ALL" | CategoryId;
const FILTERS: Filter[] = ["ALL", "OUTCOME", "DISCIPLINE", "TERRITORY", "PLAYER"];

export function BuildYourOwn({ slip }: BuildYourOwnProps) {
  const [filter, setFilter] = useState<Filter>("ALL");
  const [added, setAdded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  // Phase I5.3 — per-row live score state from super-acca-byo-score.
  const [scores, setScores] = useState<Record<string, ScoreState>>({});

  const scoreCandidate = async (m: BYOMarketCandidate): Promise<void> => {
    if (m.odds === null) return;
    setScores((s) => ({ ...s, [m.id]: { kind: "loading" } }));
    try {
      const sb = getBrowserClient();
      const { data, error } = await sb.functions.invoke("super-acca-byo-score", {
        body: {
          slip_id: slip.id,
          // The view-model collapses market+selection into a single
          // human-readable `market` string; the scorer accepts both.
          proposed_market: m.market,
          proposed_selection: m.market,
          proposed_odds: m.odds,
        },
      });
      if (error) {
        setScores((s) => ({
          ...s,
          [m.id]: { kind: "error", message: error.message ?? "Score request failed." },
        }));
        return;
      }
      const score = (data ?? {}) as ByoScore;
      setScores((s) => ({ ...s, [m.id]: { kind: "ok", score } }));
    } catch (e) {
      setScores((s) => ({
        ...s,
        [m.id]: {
          kind: "error",
          message: e instanceof Error ? e.message : "Score request failed.",
        },
      }));
    }
  };

  const list = useMemo(
    () =>
      filter === "ALL"
        ? slip.build_your_own_legs
        : slip.build_your_own_legs.filter((m) => m.category === filter),
    [slip.build_your_own_legs, filter],
  );

  const totalOdds = useMemo(() => {
    return added.reduce((acc, id) => {
      const m = slip.build_your_own_legs.find((x) => x.id === id);
      if (!m || m.state === "contradicts" || !m.odds) return acc;
      return acc * m.odds;
    }, slip.combined_odds);
  }, [added, slip.build_your_own_legs, slip.combined_odds]);

  const toggleAdd = (id: string) => {
    const m = slip.build_your_own_legs.find((x) => x.id === id);
    if (!m || m.state === "contradicts") return;
    // Live-score this leg via the I2 BYO scorer. Persisting the lock
    // remains a TODO (needs a separate write endpoint that mutates
    // bet_builder_slips.build_your_own_legs).
    const live = scores[id];
    if (!live || live.kind === "error") {
      void scoreCandidate(m);
    }
    // Optimistic local toggle so the combined-odds preview still feels
    // responsive. If the scorer reports compatible:false on return, the
    // detail pane surfaces the contradiction inline.
    setAdded((a) =>
      a.includes(id) ? a.filter((x) => x !== id) : a.length < 3 ? [...a, id] : a,
    );
  };

  return (
    <div
      style={{
        marginTop: 28,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--sa-hairline)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow>Build-Your-Own · Intelligence Layer</Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 22,
              fontWeight: 600,
              marginTop: 8,
              color: "var(--sa-fg-primary)",
            }}
          >
            Add up to 3 custom legs · scored live
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.16em",
            }}
          >
            NEW COMBINED
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 36,
              color: "var(--sa-accent-saffron)",
              lineHeight: 1,
              marginTop: 4,
            }}
          >
            {totalOdds.toFixed(2)}×
          </div>
        </div>
      </div>

      {slip.build_your_own_legs.length === 0 ? (
        <div style={{ padding: 24 }}>
          <p
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 13.5,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            BYO market library queued — the scorer publishes candidates after
            the slip's contradiction engine clears.
          </p>
        </div>
      ) : (
        <>
          <div
            style={{
              padding: "14px 24px",
              borderBottom: "1px solid var(--sa-hairline)",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                style={{
                  background: filter === f ? "var(--sa-amber-glow)" : "transparent",
                  color:
                    filter === f ? "var(--sa-accent-saffron)" : "var(--sa-fg-secondary)",
                  border: `1px solid ${filter === f ? "var(--sa-amber-dim)" : "var(--sa-hairline)"}`,
                  padding: "6px 12px",
                  borderRadius: 3,
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 10.5,
                  letterSpacing: "0.16em",
                  cursor: "pointer",
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.1fr 1fr",
              minHeight: 320,
            }}
          >
            <div style={{ borderRight: "1px solid var(--sa-hairline)" }}>
              {list.map((m) => (
                <BYORow
                  key={m.id}
                  m={m}
                  isSelected={selected === m.id}
                  isAdded={added.includes(m.id)}
                  scoreState={scores[m.id] ?? { kind: "idle" }}
                  onSelect={() => {
                    setSelected(m.id);
                    if (!scores[m.id]) void scoreCandidate(m);
                  }}
                  onToggle={() => toggleAdd(m.id)}
                />
              ))}
            </div>
            <div style={{ padding: 22 }}>
              {!selected ? (
                <p
                  style={{
                    fontFamily: "var(--sa-font-body)",
                    fontSize: 13,
                    color: "var(--sa-fg-muted)",
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  Tap any market on the left. The engine returns Contradiction
                  check · EV impact · Value Edge · Sharp Money signal · Model
                  vs Implied probability.
                </p>
              ) : (
                <BYODetail
                  m={slip.build_your_own_legs.find((x) => x.id === selected)!}
                  added={added.includes(selected)}
                  scoreState={scores[selected] ?? { kind: "idle" }}
                  onToggle={() => toggleAdd(selected!)}
                />
              )}
            </div>
          </div>
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid var(--sa-hairline)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 11,
                color: "var(--sa-fg-secondary)",
              }}
            >
              {added.length}/3 CUSTOM LEGS · GH₵100 →{" "}
              <span style={{ color: "var(--sa-accent-saffron)" }}>
                GH₵{Math.round(100 * totalOdds).toLocaleString()}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BYORow({
  m,
  isSelected,
  isAdded,
  scoreState,
  onSelect,
  onToggle,
}: {
  m: BYOMarketCandidate;
  isSelected: boolean;
  isAdded: boolean;
  scoreState: ScoreState;
  onSelect: () => void;
  onToggle: () => void;
}) {
  // Live scorer override — if the I2 endpoint flags incompatibility,
  // mark the row as blocked even if the precomputed candidate said edge.
  const liveContradicts =
    scoreState.kind === "ok" && scoreState.score.compatible === false;
  const blocked = m.state === "contradicts" || liveContradicts;
  const stateColor =
    blocked
      ? "var(--sa-signal-red)"
      : m.state === "edge"
        ? "var(--sa-signal-green)"
        : "var(--sa-fg-primary)";
  const buttonLabel =
    scoreState.kind === "loading"
      ? "SCORING…"
      : blocked
        ? "BLOCKED"
        : isAdded
          ? "✓ ADDED"
          : "+ ADD";
  return (
    <div
      onClick={onSelect}
      style={{
        padding: "14px 22px",
        borderBottom: "1px solid var(--sa-hairline)",
        background: isSelected ? "var(--sa-bg-raised)" : "transparent",
        borderLeft: `3px solid ${isSelected ? "var(--sa-accent-saffron)" : "transparent"}`,
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <CategoryChip category={m.category} short />
          {m.state === "contradicts" && m.conflicts_with !== null ? (
            <Badge variant="danger">CONTRADICTS L0{m.conflicts_with}</Badge>
          ) : null}
          {liveContradicts && m.state !== "contradicts" ? (
            <Badge variant="danger">LIVE: BLOCKED</Badge>
          ) : null}
          {m.state === "edge" && m.edge !== null ? (
            <Badge variant="success">+{m.edge}pp EDGE</Badge>
          ) : null}
          {scoreState.kind === "error" ? (
            <Badge variant="warn">SCORE ERR</Badge>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 14,
            color: "var(--sa-fg-primary)",
            marginTop: 6,
          }}
        >
          {m.market}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        {m.odds !== null ? (
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 14,
              color: stateColor,
            }}
          >
            {m.odds.toFixed(2)}×
          </div>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          style={{
            marginTop: 6,
            padding: "4px 10px",
            fontSize: 10,
            fontFamily: "var(--sa-font-mono)",
            letterSpacing: "0.14em",
            background: isAdded && !blocked ? "var(--sa-accent-saffron)" : "transparent",
            color: isAdded && !blocked
              ? "var(--sa-bg-base)"
              : blocked
                ? "var(--sa-signal-red)"
                : "var(--sa-fg-primary)",
            border: `1px solid ${
              isAdded && !blocked
                ? "var(--sa-accent-saffron)"
                : blocked
                  ? "var(--sa-signal-red)"
                  : "var(--sa-hairline)"
            }`,
            borderRadius: 2,
            cursor: blocked || scoreState.kind === "loading" ? "not-allowed" : "pointer",
            opacity: scoreState.kind === "loading" ? 0.7 : 1,
          }}
          disabled={blocked || scoreState.kind === "loading"}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

function BYODetail({
  m,
  added,
  scoreState,
  onToggle,
}: {
  m: BYOMarketCandidate;
  added: boolean;
  scoreState: ScoreState;
  onToggle: () => void;
}) {
  const live = scoreState.kind === "ok" ? scoreState.score : null;
  const liveBlocked = live?.compatible === false;
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.18em",
        }}
      >
        {CATEGORY[m.category].label}
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-display)",
          fontSize: 20,
          fontWeight: 600,
          marginTop: 6,
          color: "var(--sa-fg-primary)",
        }}
      >
        {m.market}
      </div>
      {scoreState.kind === "loading" ? (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: "var(--sa-bg-raised)",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-secondary)",
            letterSpacing: "0.16em",
          }}
        >
          SCORING LIVE…
        </div>
      ) : null}
      {scoreState.kind === "error" ? (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: "var(--sa-bg-raised)",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-signal-red)",
          }}
        >
          SCORE ERROR · {scoreState.message}
        </div>
      ) : null}
      {m.state === "contradicts" || liveBlocked ? (
        <div
          style={{
            marginTop: 16,
            padding: 14,
            background: "var(--sa-signal-red-dim)",
            border: "1px solid var(--sa-signal-red)",
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-signal-red)",
              letterSpacing: "0.16em",
            }}
          >
            ⊘ CONTRADICTION ENGINE
            {liveBlocked && live?.contradicts_with_leg
              ? ` · L0${live.contradicts_with_leg}`
              : ""}
          </div>
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--sa-font-body)",
              fontSize: 13,
              color: "var(--sa-fg-primary)",
              lineHeight: 1.55,
            }}
          >
            {(liveBlocked ? live?.reason : m.reason) ??
              "Contradicts an existing leg in this slip."}
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 10,
              marginTop: 16,
            }}
          >
            <ScorePill label="MODEL" value={m.model !== null ? `${m.model}%` : "—"} />
            <ScorePill
              label="MARKET"
              value={m.implied !== null ? `${m.implied}%` : "—"}
              muted
            />
            <ScorePill
              label="EDGE"
              value={
                m.edge !== null ? `${m.edge > 0 ? "+" : ""}${m.edge}pp` : "—"
              }
              accent={(m.edge ?? 0) > 0}
            />
            <ScorePill label="EV IMPACT" value={m.ev ?? "—"} accent={!!m.ev?.startsWith("+")} />
          </div>
          {m.sharp ? (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                background: "var(--sa-bg-raised)",
                border: "1px solid var(--sa-hairline)",
                borderRadius: 4,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 10,
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.16em",
                }}
              >
                SHARP MONEY
              </span>
              <Badge
                variant={
                  m.sharp === "CONFIRMS"
                    ? "success"
                    : m.sharp === "CHALLENGES"
                      ? "warn"
                      : "ghost"
                }
              >
                {m.sharp}
              </Badge>
            </div>
          ) : null}
          {m.note ? (
            <p
              style={{
                fontFamily: "var(--sa-font-body)",
                fontSize: 13,
                color: "var(--sa-fg-secondary)",
                lineHeight: 1.6,
                marginTop: 14,
              }}
            >
              {m.note}
            </p>
          ) : null}
          <button
            type="button"
            onClick={onToggle}
            style={{
              marginTop: 14,
              width: "100%",
              padding: "12px 14px",
              background: added ? "var(--sa-accent-saffron)" : "transparent",
              color: added ? "var(--sa-bg-base)" : "var(--sa-accent-saffron)",
              border: "1px solid var(--sa-accent-saffron)",
              borderRadius: 3,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              cursor: "pointer",
            }}
          >
            {added ? "✓ ADDED TO SLIP" : "+ ADD TO SLIP"}
          </button>
        </>
      )}
    </div>
  );
}

function ScorePill({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      style={{
        padding: 10,
        background: "var(--sa-bg-raised)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 3,
      }}
    >
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 9,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.18em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-stat)",
          fontSize: 24,
          lineHeight: 1,
          color: accent
            ? "var(--sa-signal-green)"
            : muted
              ? "var(--sa-fg-secondary)"
              : "var(--sa-fg-primary)",
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}
